import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface MyBooking {
  id: string;
  status: string;
  createdAt: string;
  event?: { id: string; title: string; startTime: string; endTime: string; image?: string; };
  business?: { id: string; name: string; slug: string; logo?: string; city?: string; category?: string; };
  service?: { name: string; price: number; };
  amount?: number;
}

const STATUS: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  CONFIRMED: { label: 'Confirmada', color: '#15803d', bg: '#dcfce7', icon: '✅' },
  PENDING:   { label: 'Pendiente',  color: '#854d0e', bg: '#fef9c3', icon: '⏳' },
  CANCELLED: { label: 'Cancelada',  color: '#dc2626', bg: '#fee2e2', icon: '❌' },
  COMPLETED: { label: 'Completada', color: '#1d4ed8', bg: '#dbeafe', icon: '🏅' },
  NO_SHOW:   { label: 'No asistí',  color: '#475569', bg: '#f1f5f9', icon: '👻' },
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' });
}
function fmtTime(d: string) {
  return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}
function isUpcoming(d: string) {
  return new Date(d) > new Date();
}

export function MyBookings() {
  const [bookings, setBookings] = useState<MyBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<'todas' | 'proximas' | 'pasadas'>('proximas');
  const [reviewModal, setReviewModal] = useState<any>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleSubmitReview = async () => {
    if (!reviewModal) return;
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_URL}/reviews/${reviewModal.businessId}`,
        { rating: reviewRating, comment: reviewComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewModal(null);
      setReviewComment('');
      setReviewRating(5);
      alert('✅ ¡Reseña publicada! Gracias por tu valoración.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al publicar la reseña');
    } finally {
      setSubmittingReview(false);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) { navigate('/login'); return; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser(payload);
    } catch {}
    loadBookings(token);
  }, []);

  const loadBookings = async (token: string) => {
    try {
      const r = await axios.get(`${API_URL}/event-bookings/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(r.data.data.bookings || []);
    } catch {
      try {
        const r2 = await axios.get(`${API_URL}/bookings/my?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBookings(r2.data.data.bookings || []);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (eventId: string) => {
    if (!confirm('¿Cancelar esta inscripción?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/event-bookings/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadBookings(token!);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al cancelar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const filtered = bookings.filter(b => {
    if (filter === 'proximas') return b.event?.startTime ? isUpcoming(b.event.startTime) : true;
    if (filter === 'pasadas') return b.event?.startTime ? !isUpcoming(b.event.startTime) : false;
    return true;
  });

  const upcoming = bookings.filter(b => b.event?.startTime && isUpcoming(b.event.startTime) && b.status !== 'CANCELLED').length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; min-height: 100vh; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

        .my-nav { background: white; border-bottom: 1px solid #e2e8f0; padding: 0 5%; height: 62px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
        .my-hero { background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); padding: 40px 5% 60px; position: relative; overflow: hidden; }
        .my-hero::before { content: ''; position: absolute; top: -30%; right: -10%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%); }
        .my-hero-inner { max-width: 680px; margin: 0 auto; position: relative; z-index: 1; }
        .my-avatar { width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; font-weight: 800; color: white; margin-bottom: 16px; }
        .my-welcome { font-size: 1.6rem; font-weight: 800; color: white; margin-bottom: 6px; }
        .my-sub { font-size: .88rem; color: #94a3b8; margin-bottom: 24px; }
        .my-stats { display: flex; gap: 16px; flex-wrap: wrap; }
        .my-stat { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.1); border-radius: 12px; padding: 12px 20px; }
        .my-stat-num { font-size: 1.4rem; font-weight: 800; color: white; }
        .my-stat-label { font-size: .72rem; color: #64748b; margin-top: 2px; }

        .my-body { max-width: 680px; margin: -24px auto 0; padding: 0 20px 40px; position: relative; z-index: 1; animation: fadeUp .3s ease; }
        .filter-bar { background: white; border-radius: 14px; padding: 6px; display: flex; gap: 4px; margin-bottom: 20px; box-shadow: 0 4px 16px rgba(0,0,0,.08); }
        .filter-btn { flex: 1; padding: 10px; border-radius: 10px; border: none; font-weight: 600; font-size: .82rem; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; background: transparent; color: #64748b; }
        .filter-btn.active { background: #3b82f6; color: white; box-shadow: 0 2px 8px rgba(59,130,246,.3); }

        .bk-card { background: white; border-radius: 18px; border: 1px solid #e2e8f0; overflow: hidden; margin-bottom: 14px; transition: transform .2s, box-shadow .2s; }
        .bk-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
        .bk-img { height: 140px; background: linear-gradient(135deg, #1e293b, #334155); position: relative; overflow: hidden; }
        .bk-img img { width: 100%; height: 100%; object-fit: cover; }
        .bk-img-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,.6) 0%, transparent 50%); }
        .bk-img-date { position: absolute; top: 12px; left: 12px; background: white; border-radius: 10px; padding: 6px 10px; text-align: center; }
        .bk-img-day { font-size: 1.2rem; font-weight: 900; color: #1e293b; line-height: 1; }
        .bk-img-mon { font-size: .6rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
        .bk-body { padding: 16px 18px; }
        .bk-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
        .bk-title { font-size: .95rem; font-weight: 800; color: #1e293b; line-height: 1.3; }
        .bk-biz { display: flex; align-items: center; gap: 6px; font-size: .75rem; color: #3b82f6; font-weight: 600; margin-bottom: 10px; }
        .bk-biz-logo { width: 20px; height: 20px; border-radius: 5px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; color: white; font-size: .6rem; font-weight: 700; overflow: hidden; flex-shrink: 0; }
        .bk-biz-logo img { width: 100%; height: 100%; object-fit: cover; }
        .bk-meta { font-size: .78rem; color: #64748b; display: flex; flex-direction: column; gap: 3px; margin-bottom: 14px; }
        .bk-footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .bk-actions { display: flex; gap: 8px; }
        .btn-view { padding: 8px 16px; border-radius: 8px; background: #eff6ff; color: #1d4ed8; font-size: .78rem; font-weight: 700; text-decoration: none; display: inline-block; }
        .btn-cancel-sm { padding: 8px 14px; border-radius: 8px; border: 1px solid #fee2e2; background: white; color: #dc2626; font-size: .78rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; }
        .btn-logout { padding: 8px 16px; border-radius: 8px; border: 1px solid #e2e8f0; background: white; color: #64748b; font-size: .82rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; }
        .btn-logout:hover { background: #fef2f2; color: #dc2626; border-color: #fee2e2; }
        .spinner { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 48px auto; }
        .empty { text-align: center; padding: 48px 24px; background: white; border-radius: 18px; border: 1px solid #e2e8f0; }
        .past-badge { position: absolute; bottom: 12px; left: 12px; background: rgba(0,0,0,.6); color: white; font-size: .68rem; font-weight: 700; padding: 4px 10px; border-radius: 100px; backdrop-filter: blur(4px); }
      `}</style>

      {/* NAV */}
      <div className="my-nav">
        <a href="https://fitpulse.es" style={{ fontWeight: 900, fontSize: '1.1rem', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, background: '#3b82f6', borderRadius: '50%', display: 'inline-block' }}></span>
          FitPulse
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a href="https://fitpulse.es" style={{ fontSize: '.82rem', color: '#64748b', textDecoration: 'none', fontWeight: 500 }}>Explorar →</a>
          <button onClick={handleLogout} className="btn-logout">Cerrar sesión</button>
        </div>
      </div>

      {/* HERO */}
      <div className="my-hero">
        <div className="my-hero-inner">
          <div className="my-avatar">{user?.email?.charAt(0).toUpperCase() || '👤'}</div>
          <div className="my-welcome">Hola, {user?.firstName || user?.email?.split('@')[0] || 'deportista'} 👋</div>
          <div className="my-sub">Aquí tienes todas tus inscripciones a eventos</div>
          <div className="my-stats">
            <div className="my-stat">
              <div className="my-stat-num">{bookings.filter(b => b.status !== 'CANCELLED').length}</div>
              <div className="my-stat-label">Total inscritas</div>
            </div>
            <div className="my-stat">
              <div className="my-stat-num">{upcoming}</div>
              <div className="my-stat-label">Próximas</div>
            </div>
            <div className="my-stat">
              <div className="my-stat-num">{bookings.filter(b => b.status === 'COMPLETED').length}</div>
              <div className="my-stat-label">Completadas</div>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="my-body">
        {/* FILTROS */}
        <div className="filter-bar">
          {([['proximas','⚡ Próximas'],['todas','📋 Todas'],['pasadas','🏅 Pasadas']] as [string,string][]).map(([k,l]) => (
            <button key={k} className={`filter-btn ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k as any)}>{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : filtered.length > 0 ? (
          filtered.map(b => {
            const st = STATUS[b.status] || STATUS.PENDING;
            const d = b.event?.startTime ? new Date(b.event.startTime) : null;
            const past = d ? !isUpcoming(b.event!.startTime) : false;
            return (
              <div key={b.id} className="bk-card">
                {/* Imagen */}
                <div className="bk-img" style={{ position: 'relative' }}>
                  {b.event?.image
                    ? <img src={b.event.image} alt={b.event.title} />
                    : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1e3a8a, #1e293b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>📅</div>
                  }
                  <div className="bk-img-overlay"></div>
                  {d && (
                    <div className="bk-img-date">
                      <div className="bk-img-day">{d.getDate()}</div>
                      <div className="bk-img-mon">{d.toLocaleDateString('es-ES', { month: 'short' })}</div>
                    </div>
                  )}
                  {past && <div className="past-badge">Evento pasado</div>}
                </div>

                <div className="bk-body">
                  <div className="bk-header">
                    <div className="bk-title">{b.event?.title || b.service?.name || 'Evento'}</div>
                    <span style={{ background: st.bg, color: st.color, fontSize: '.68rem', fontWeight: 800, padding: '4px 10px', borderRadius: 100, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {st.icon} {st.label}
                    </span>
                  </div>

                  {b.business && (
                    <div className="bk-biz">
                      <div className="bk-biz-logo">{b.business.logo ? <img src={b.business.logo} alt={b.business.name} /> : b.business.name.charAt(0)}</div>
                      {b.business.name}{b.business.city ? ` · ${b.business.city}` : ''}
                      {b.business.category && <span style={{ background: '#eff6ff', color: '#3b82f6', fontSize: '.65rem', padding: '2px 8px', borderRadius: 100, marginLeft: 4 }}>{b.business.category}</span>}
                    </div>
                  )}

                  <div className="bk-meta">
                    {d && <span>📅 {fmtDate(b.event!.startTime)} · 🕐 {fmtTime(b.event!.startTime)}</span>}
                    <span>{b.amount || b.service?.price ? `💶 ${b.amount || b.service?.price}€` : '🆓 Gratis'}</span>
                    <span style={{ color: '#94a3b8', fontSize: '.72rem' }}>Inscrito el {new Date(b.createdAt).toLocaleDateString('es-ES')}</span>
                  </div>

                  <div className="bk-footer">
                    <div className="bk-actions">
                      {b.business?.slug && (
                        <a href={`/b/${b.business.slug}`} className="btn-view">Ver negocio →</a>
                      )}
                    </div>
                    {b.status === 'CONFIRMED' && b.event?.id && !past && (
                      <button className="btn-cancel-sm" onClick={() => handleCancel(b.event!.id)}>Cancelar</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty">
            <div style={{ fontSize: 56, marginBottom: 16 }}>
              {filter === 'proximas' ? '📅' : filter === 'pasadas' ? '🏅' : '📋'}
            </div>
            <p style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
              {filter === 'proximas' ? 'Sin eventos próximos' : filter === 'pasadas' ? 'Sin eventos pasados' : 'Sin inscripciones'}
            </p>
            <p style={{ fontSize: '.85rem', color: '#64748b', marginBottom: 24 }}>
              {filter === 'proximas' ? 'Explora negocios y apúntate a tus actividades favoritas' : 'Aquí aparecerán tus eventos cuando se celebren'}
            </p>
            {filter !== 'pasadas' && (
              <a href="https://fitpulse.es" style={{ background: '#3b82f6', color: 'white', padding: '12px 24px', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: '.88rem', display: 'inline-block' }}>
                Explorar eventos →
              </a>
            )}
          </div>
        )}
      </div>
      {/* MODAL RESEÑA */}
      {reviewModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20,backdropFilter:'blur(4px)'}}>
          <div style={{background:'white',borderRadius:20,padding:28,maxWidth:420,width:'100%',boxShadow:'0 40px 100px rgba(0,0,0,.25)'}}>
            <div style={{fontWeight:800,fontSize:'1.1rem',color:'#1e293b',marginBottom:4}}>⭐ Valorar negocio</div>
            <div style={{fontSize:'.82rem',color:'#64748b',marginBottom:20}}>{reviewModal.businessName}</div>

            <div style={{marginBottom:16}}>
              <p style={{fontSize:'.82rem',fontWeight:600,color:'#475569',marginBottom:8}}>Tu valoración</p>
              <div style={{display:'flex',gap:8}}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewRating(s)}
                    style={{fontSize:'2rem',background:'none',border:'none',cursor:'pointer',color:s<=reviewRating?'#f59e0b':'#e2e8f0',transition:'color .15s',padding:0}}>★</button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:20}}>
              <p style={{fontSize:'.82rem',fontWeight:600,color:'#475569',marginBottom:8}}>Comentario (opcional)</p>
              <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                placeholder="Cuéntanos tu experiencia..."
                style={{width:'100%',padding:'12px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'.88rem',fontFamily:'Inter,sans-serif',resize:'none',outline:'none'}}
                rows={3}/>
            </div>

            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setReviewModal(null)}
                style={{flex:1,padding:'11px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'Inter,sans-serif'}}>
                Cancelar
              </button>
              <button onClick={handleSubmitReview} disabled={submittingReview}
                style={{flex:2,padding:'11px',borderRadius:10,border:'none',background:'#3b82f6',color:'white',fontWeight:700,fontSize:'.85rem',cursor:'pointer',fontFamily:'Inter,sans-serif'}}>
                {submittingReview ? 'Publicando...' : '✅ Publicar reseña'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
